import type { Blackout } from "./api";

/** Monday is 0, as the API counts (rule A-4). */
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MAX_BLACKOUTS = 14;
const MONTH_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The day in the new month: the same day, or the last day of a shorter month. */
export function keptDay(month: number, day: number): number {
  return Math.min(Number.isInteger(day) && day > 0 ? day : 1, MONTH_DAYS[month - 1] ?? 31);
}

/** A typed time as 24-hour "HH:MM" ("7" and "0700" give "07:00"), or the text as typed when it is not a time. */
export function clockText(text: string): string {
  const match = /^(\d{1,2})(?::?(\d{2}))?$/.exec(text.trim());
  if (!match) return text;
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  return hours < 24 && minutes < 60 ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}` : text;
}

/** "MM-DD" from a month (1 to 12) and a day, or null when no year has that day. */
export function monthDay(month: number, day: number): string | null {
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  if (!Number.isInteger(day) || day < 1 || day > MONTH_DAYS[month - 1]) return null;
  return `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function monthDayText(value: string): string {
  const [month, day] = value.split("-").map(Number);
  return `${MONTHS[month - 1]} ${day}`;
}

export function seasonText(season: [string, string] | null | undefined): string {
  return season ? `${monthDayText(season[0])} to ${monthDayText(season[1])}` : "All year";
}

export function blackoutText(found: readonly Blackout[] | undefined): string {
  if (!found?.length) return "None";
  return [...found]
    .sort((a, b) => a.day - b.day || a.start.localeCompare(b.start))
    .map((item) => `${DAYS[item.day]} ${item.start}–${item.end}`)
    .join("; ");
}

/** The first problem of these blackout windows, or null. */
export function blackoutProblem(found: readonly Blackout[]): string | null {
  if (found.length > MAX_BLACKOUTS) return `Give at most ${MAX_BLACKOUTS} blackout windows.`;
  for (const item of found) {
    if (!item.start || !item.end) return `Give the start and the end of the blackout window on ${DAYS[item.day]}.`;
    if (!CLOCK.test(item.start) || !CLOCK.test(item.end)) return `Give the times of the blackout window on ${DAYS[item.day]} as HH:MM, for example 13:30.`;
    if (item.start >= item.end) return `The blackout window on ${DAYS[item.day]} must end after it starts.`;
  }
  const ordered = [...found].sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));
  for (let i = 1; i < ordered.length; i++) {
    const before = ordered[i - 1];
    const after = ordered[i];
    if (before.day === after.day && after.start < before.end) return `Two blackout windows on ${DAYS[before.day]} overlap.`;
  }
  return null;
}

/** The chip text of an account out of its season, with the day the season opens. */
export function offSeasonText(projection: { season_opens?: string | null }): string {
  const opens = projection.season_opens;
  return opens ? `Off season until ${monthDayText(opens.slice(5, 10))}` : "Off season";
}
